/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RoundContext } from '../contexts';
import { FeedbackPost, TaskPost } from '../../config/interfaces';

// TODO: Need to define a round payload not use any
// interface ICurrentRoundPayload {}

interface IGetRoundPayload {
  roundId?: string;
  fromHistoryDrawer: boolean;
  type?: string;
  accountNum?: string;
  unitId?: string;
}

/**
 * Redux slice for managing the state of patient rounds using Redux Toolkit's `createSlice`.
 *
 * This slice handles all state transitions related to the lifecycle of a round, including:
 * - Starting and completing a round
 * - Setting and clearing the current round
 * - Posting a round and handling its success or failure
 * - Fetching a round and handling its success or failure
 * - Managing pending issues and feedbacks associated with a round
 * - Resetting the round state to its initial values
 *
 * @property {object} initialState - The initial state for the round context.
 * @property {Function} setRoundStarted - Marks a round as started and not completed.
 * @property {Function} setCurrentRound - Sets the current round details.
 * @property {Function} postRound - Initiates the posting of a round and sets loading state.
 * @property {Function} postRoundSuccess - Handles successful round posting, resets relevant state.
 * @property {Function} postRoundFailure - Handles failed round posting, resets loading and pending states.
 * @property {Function} getRound - Initiates fetching of a round and sets fetching state.
 * @property {Function} getRoundSuccess - Handles successful round fetch, updates current round.
 * @property {Function} getRoundFailure - Handles failed round fetch, resets fetching state.
 * @property {Function} setPendingIssue - Adds a pending issue to the round.
 * @property {Function} setPendingFeedback - Adds pending feedback to the round.
 * @property {Function} clearRoundState - Resets the round state to its initial values.
 *
 * @example
 * dispatch(setRoundStarted());
 * dispatch(setCurrentRound({ id: 1, ... }));
 *
 * @returns {object} The round slice reducer and actions.
 */
const roundSlice = createSlice({
  name: 'round',
  initialState: RoundContext,
  reducers: {
    setRoundStarted: state => {
      return {
        ...state,
        isRoundStarted: true,
        isRoundCompleted: false
      };
    },
    setCurrentRound: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        currentRound: { ...action.payload }
      };
    },
    postRound: (state, action: PayloadAction<{ type: string }>) => {
      return {
        ...state,
        loading: true
      };
    },
    postRoundSuccess: state => {
      return {
        ...state,
        currentRound: {} as any,
        isRoundStarted: false,
        isRoundCompleted: true,
        loading: false,
        pendingFeedback: [],
        pendingIssue: []
      };
    },
    postRoundFailure: state => {
      return {
        ...state,
        loading: false,
        pendingFeedback: [],
        pendingIssue: []
      };
    },
    getRound: (state, action: PayloadAction<IGetRoundPayload>) => {
      return {
        ...state,
        fetchingRound: true
      };
    },
    getRoundSuccess: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        currentRound: { ...action.payload },
        fetchingRound: false
      };
    },
    getRoundFailure: state => {
      return {
        ...state,
        fetchingRound: false
      };
    },
    setPendingIssue: (state, action: PayloadAction<TaskPost>) => {
      return {
        ...state,
        pendingIssues: [...state.pendingIssues, { ...action.payload }]
      };
    },
    setPendingFeedback: (state, action: PayloadAction<FeedbackPost>) => {
      return {
        ...state,
        pendingFeedbacks: [...state.pendingFeedbacks, { ...action.payload }]
      };
    },
    clearRoundState: state => {
      return {
        ...state,
        currentRound: {} as any,
        isRoundStarted: false,
        isRoundCompleted: false,
        loading: false,
        pendingFeedbacks: [],
        pendingIssues: []
      };
    }
  }
});

export const {
  setRoundStarted,
  setCurrentRound,
  postRound,
  postRoundSuccess,
  postRoundFailure,
  getRound,
  getRoundSuccess,
  getRoundFailure,
  setPendingIssue,
  setPendingFeedback,
  clearRoundState
} = roundSlice.actions;

export default roundSlice.reducer;
